<script setup lang="ts">
  import { computed, ref } from 'vue';
  import type { DestinationPrices } from '@/entities/searchCriteria';
  import type { SearchFormGoal, SearchFormTracking } from '@/entities/searchForm';
  import type { AccountUser } from '@/features/account';
  import { SerpLayout, useSerpCriteria } from '@/layouts/serp';
  import { STAY_CALENDAR_AB_TEST } from '@/shared/config/abTests';
  import { CountryResortsUrl, FormDataUrl, IntelligentSearchUrl } from '@/shared/config/formData';
  import { abTests } from '@/shared/lib/AbTests';
  import { SearchForm, type SearchFormCriteria, type SearchFormExpose } from '@/widgets/searchForm';

  // Повторяет SerpPageProps из layouts/serp: compiler-sfc Vue 2.7 не резолвит импортированный
  // тип в defineProps, поэтому контракт страницы объявлен здесь.
  interface HotelPageProps {
    /**
     * Отельная — одна страница с тремя режимами, и режим зависит от того, откуда пришёл
     * пользователь, а не от адреса. Поэтому здесь он проп, а не свойство страницы.
     */
    flow?: 'tourOffer' | 'hotelOffer' | 'railwayTour';
    modelValue?: SearchFormCriteria | null;
    user?: AccountUser | null;
    accountUrl?: string;
    prices?: DestinationPrices;
    isVisibleRoomButton?: boolean;
  }

  const props = withDefaults(defineProps<HotelPageProps>(), {
    flow: 'tourOffer',
    modelValue: null,
    isVisibleRoomButton: false,
  });

  interface HotelPageEmits {
    (e: 'update:modelValue', criteria: SearchFormCriteria): void;
    (e: 'search', criteria: SearchFormCriteria): void;
    (e: 'track', goal: SearchFormGoal): void;
    (e: 'login'): void;
    (e: 'logout'): void;
    (e: 'buy'): void;
  }
  const emit = defineEmits<HotelPageEmits>();

  interface FlowTexts {
    dateFieldLabel?: string;
    touristsFieldLabel?: string;
    touristsWordForms?: string[];
    nightsSummaryPrefix?: string;
    searchBtnText?: string;
  }

  // Пустой набор = дефолты формы, то есть туровые подписи.
  const FLOW_TEXTS: Record<NonNullable<HotelPageProps['flow']>, FlowTexts> = {
    tourOffer: {},
    hotelOffer: {
      dateFieldLabel: 'Дата заезда',
      touristsFieldLabel: 'Гости',
      touristsWordForms: ['гостя', 'гостей', 'гостей'],
      nightsSummaryPrefix: 'С',
      searchBtnText: 'Найти отели',
    },
    railwayTour: { dateFieldLabel: 'Дата тура' },
  };

  interface FlowDictionaries {
    formUrl?: string;
    searchUrl?: string;
    resortsUrl?: string;
  }

  // Пустой набор = словари из AppConfig отельной (slug=hotel_page). Там туровые направления,
  // поэтому ж/д-флоу предлагал бы страны без ж/д-туров.
  const FLOW_DICTIONARIES: Record<NonNullable<HotelPageProps['flow']>, FlowDictionaries> = {
    tourOffer: {},
    hotelOffer: {},
    railwayTour: {
      formUrl: FormDataUrl.Railway,
      searchUrl: IntelligentSearchUrl.Railway,
      resortsUrl: CountryResortsUrl.Railway,
    },
  };

  const FLOW_TRACKING: Record<NonNullable<HotelPageProps['flow']>, SearchFormTracking | undefined> = {
    tourOffer: { pageType: 'hotel', productType: 'tour' },
    hotelOffer: { pageType: 'hotel', productType: 'hotel' },
    railwayTour: undefined,
  };

  const searchFormRef = ref<SearchFormExpose | null>(null);

  // В отельном флоу направление зафиксировано этим отелем: «Куда» остаётся видимым, но неактивным.
  const isHotelOffersFlow = computed(() => props.flow === 'hotelOffer');
  const isStayCalendar = computed(() => isHotelOffersFlow.value && abTests.hasTest(STAY_CALENDAR_AB_TEST));
  const criteria = useSerpCriteria(props, emit, isStayCalendar);

  const texts = computed(() => FLOW_TEXTS[props.flow]);
  const dictionaries = computed(() => FLOW_DICTIONARIES[props.flow]);
  // Многокомнатность только в туровом флоу: в ж/д её нет, в отельном гостей выбирают как на серпе отелей.
  const roomButtonVisible = computed(() => props.flow === 'tourOffer' && props.isVisibleRoomButton);

  defineExpose<SearchFormExpose>({
    validate: () => (searchFormRef.value ? searchFormRef.value.validate() : true),
    submit: () => searchFormRef.value?.submit(),
    reset: () => searchFormRef.value?.reset(),
  });
</script>

<script lang="ts">
  export default {
    model: {
      prop: 'modelValue',
      event: 'update:modelValue',
    },
  };
</script>

<template>
  <SerpLayout
    :user="user"
    :account-url="accountUrl"
    @login="emit('login')"
    @logout="emit('logout')"
    @buy="emit('buy')"
  >
    <template #form="{ collapsed }">
      <!-- Критерии на отельной уже выбраны: смена города или направления не должна сбрасывать дату и ночи. -->
      <SearchForm
        ref="searchFormRef"
        v-model="criteria"
        :rounded-bottom="collapsed"
        :prices="prices"
        :is-visible-room-button="roomButtonVisible"
        :destination-disabled="isHotelOffersFlow"
        :disable-criteria-defaults="true"
        :form-url="dictionaries.formUrl"
        :search-url="dictionaries.searchUrl"
        :resorts-url="dictionaries.resortsUrl"
        :date-field-label="texts.dateFieldLabel"
        :tourists-field-label="texts.touristsFieldLabel"
        :tourists-word-forms="texts.touristsWordForms"
        :nights-summary-prefix="texts.nightsSummaryPrefix"
        :search-btn-text="texts.searchBtnText"
        :is-stay-calendar="isStayCalendar"
        :tracking="FLOW_TRACKING[flow]"
        @search="emit('search', $event)"
        @track="emit('track', $event)"
      />
    </template>
  </SerpLayout>
</template>
